// Apolo IDE — useSelfRepair (Self-Repair Loop, E4 / WS 4.1).
//
// The renderer-side glue for the post-turn self-repair loop. It owns two things:
//   1. the DEFAULT-OFF toggle (localStorage `apolo_self_repair`) — opt-in, so an
//      unset/false flag means today's behavior is byte-identical (zero change);
//   2. the LIVE view state (`state` + `events`) the SelfRepairPanel renders, fed
//      by the driver's `onEvent` callback.
//
// FRAMEWORK-LIGHT BY CONTRACT: this hook imports NOTHING from Electron and does
// NOT import `runLocalAgentLoop` or the driver (`selfRepairLoop`). The real
// driver/checker/runAgent are injected as `deps` from the composition root
// (SessionWorkspace), keeping the Dependency Rule intact — the UI depends on a
// PORT, never on a concretion. `runFromTurn` returns IMMEDIATELY when the toggle
// is off (the post-turn no-op that guarantees OFF ⇒ zero behavior change).

import { useCallback, useEffect, useRef, useState } from 'react';
import { STATES } from '../lib/selfRepair/selfRepairMachine.js';

export const LS_SELF_REPAIR = 'apolo_self_repair';

// DEFAULT ON (wire-dead-engines E5): an ABSENT flag — or anything other than the literal
// '0'/'false' — means ENABLED, so a fresh install gets the post-turn self-repair loop out
// of the box (Cursor "self-test until validated"). Setting '0'/'false' is the kill-switch
// (the toggle button writes exactly that), restoring byte-identical pre-sprint behavior.
// No window (SSR/Node) ⇒ off, since there's no renderer to repair anyway. Tolerant of
// private-mode / quota throws.
export function readSelfRepairEnabled() {
  if (typeof window === 'undefined') return false;
  try {
    const raw = window.localStorage.getItem(LS_SELF_REPAIR);
    if (raw === null || raw === undefined) return true; // DEFAULT ON
    return !(raw === '0' || raw === 'false');
  } catch {
    return true;
  }
}

export function writeSelfRepairEnabled(value) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(LS_SELF_REPAIR, value ? '1' : '0');
  } catch {
    // ignore — private mode / quota
  }
}

export function useSelfRepair() {
  const [enabled, setEnabled] = useState(readSelfRepairEnabled);
  // Live machine view: `state` is the current stage; `events` is the ordered
  // log the panel can show. Both are driven purely by the driver's onEvent.
  const [state, setState] = useState(STATES.PROPOSED);
  const [events, setEvents] = useState([]);

  // Mirror the toggle into localStorage on change.
  useEffect(() => {
    writeSelfRepairEnabled(enabled);
  }, [enabled]);

  const toggle = useCallback(() => setEnabled((v) => !v), []);

  // The sink the driver calls per transition. Each event carries at least
  // { type:'self_repair', state, cycle, evidence? } (03-PLAN §6). We keep the
  // log append-only and snap `state` to whatever the latest event reports.
  const onEvent = useCallback((event) => {
    if (!event) return;
    setEvents((prev) => [...prev, event]);
    if (event.state) setState(event.state);
  }, []);

  // Keep the freshest enabled flag readable inside the async runFromTurn without
  // re-creating the callback every toggle (and without a stale closure).
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  // Called by SessionWorkspace after a turn completes. Returns IMMEDIATELY (a
  // no-op) when the toggle is off — this is the guarantee that OFF ⇒ zero
  // behavior change. When on, it resets the live view and hands the run to the
  // injected driver, piping its events into our state.
  //
  // `deps` = { selfRepairLoop, ...portArgs } supplied by the composition root.
  // We never import the driver here; we only CALL the one we're given.
  const runFromTurn = useCallback(async (turnResult, deps = {}) => {
    if (!enabledRef.current) return { ran: false };
    const loop = deps && deps.selfRepairLoop;
    if (typeof loop !== 'function') return { ran: false };

    // Fresh view for this run.
    setEvents([]);
    setState(STATES.CHECKING);

    try {
      // Compose the caller's onEvent (if any) with ours so the panel updates.
      const callerOnEvent = deps.onEvent;
      const result = await loop({
        ...deps,
        turnResult,
        onEvent: (event) => {
          onEvent(event);
          if (typeof callerOnEvent === 'function') callerOnEvent(event);
        },
      });
      if (result && result.finalState) setState(result.finalState);
      return result || { ran: false };
    } catch {
      // The driver is contractually never-throw; guard anyway so a thrown run
      // never bubbles into the turn path.
      return { ran: false };
    }
  }, [onEvent]);

  return { enabled, toggle, state, events, runFromTurn, onEvent };
}

export default useSelfRepair;
